import { useQuery } from '@tanstack/react-query';

import {
  useRequest,
  queryClient,
  type QueryFunctionOptions,
} from '@frontend/api';

import type { DxfSceneMapping, DxfSceneMappingParams } from './types';

export type GetDxfSceneMappingQueryKeyType = readonly [
  'dxfSceneMapping',
  string,
];

export const getDxfSceneMappingQueryKey = (
  partFileUuid: string,
): GetDxfSceneMappingQueryKeyType => ['dxfSceneMapping', partFileUuid] as const;

/**
 * Mapping overlay for a part file. `data` is `null` until a mapping has been
 * created.
 */
export const useGetDxfSceneMapping = (
  params: DxfSceneMappingParams,
  {
    requestOptions,
    queryOptions,
  }: QueryFunctionOptions<
    DxfSceneMapping | null,
    GetDxfSceneMappingQueryKeyType
  > = {},
) => {
  const request = useRequest(requestOptions);
  const partFileUuid = params.partFileUuid ?? '';

  return useQuery({
    ...queryOptions,
    queryKey: getDxfSceneMappingQueryKey(partFileUuid),
    enabled: Boolean(params.partFileUuid) && (queryOptions?.enabled ?? true),
    queryFn: async ({ signal }) => {
      const { mapping } = await request<{
        mapping: DxfSceneMapping | null;
      }>(`/dxf/${partFileUuid}/mapping`, {
        init: {
          signal,
        },
      });
      return mapping;
    },
  });
};

export const invalidateDxfSceneMappingQuery = (partFileUuid: string) =>
  queryClient.invalidateQueries({
    queryKey: getDxfSceneMappingQueryKey(partFileUuid),
  });
